export function geodeticToEcef(lat: number, lon: number, altKm: number = 0) {
  if (isNaN(lat) || isNaN(lon)) return { x: 0, y: 0, z: 0 };
  const latRad = (lat * Math.PI) / 180;
  const lonRad = (lon * Math.PI) / 180;
  const a = 6378.137; // WGS84 semi-major axis in km
  const e2 = 0.00669437999014; // WGS84 eccentricity squared

  const N = a / Math.sqrt(1 - e2 * Math.sin(latRad) * Math.sin(latRad));

  const x = (N + altKm) * Math.cos(latRad) * Math.cos(lonRad);
  const y = (N + altKm) * Math.cos(latRad) * Math.sin(lonRad);
  const z = (N * (1 - e2) + altKm) * Math.sin(latRad);

  return { x, y, z };
}

export function ecefToGeodetic(x: number, y: number, z: number) {
  if (isNaN(x) || isNaN(y) || isNaN(z)) return { lat: 0, lon: 0, altKm: 0 };

  // Auto-detect meters vs kilometers if coordinates are large ECEF values in meters
  let xKm = x;
  let yKm = y;
  let zKm = z;
  if (Math.hypot(x, y, z) > 100000) {
    xKm = x / 1000;
    yKm = y / 1000;
    zKm = z / 1000;
  }

  const a = 6378.137; // km
  const e2 = 0.00669437999014;
  const b = a * Math.sqrt(1 - e2);
  const ep2 = (a * a - b * b) / (b * b);

  const p = Math.sqrt(xKm * xKm + yKm * yKm);
  if (p < 1e-9) {
    return {
      lat: zKm >= 0 ? 90 : -90,
      lon: 0,
      altKm: Math.abs(zKm) - b,
    };
  }
  const theta = Math.atan2(zKm * a, p * b);

  const lon = Math.atan2(yKm, xKm);
  const lat = Math.atan2(
    zKm + ep2 * b * Math.pow(Math.sin(theta), 3),
    p - e2 * a * Math.pow(Math.cos(theta), 3)
  );

  const N = a / Math.sqrt(1 - e2 * Math.sin(lat) * Math.sin(lat));
  const altKm = p / Math.cos(lat) - N;

  return {
    lat: (lat * 180) / Math.PI,
    lon: (lon * 180) / Math.PI,
    altKm,
  };
}

export function geodeticToGeocentric(latDegrees: number): number {
  if (latDegrees === 90 || latDegrees === -90 || isNaN(latDegrees) || latDegrees == null) return latDegrees;
  const latRadians = (latDegrees * Math.PI) / 180;
  const geocentricLatRad = Math.atan(0.99330562000984 * Math.tan(latRadians));
  return (geocentricLatRad * 180) / Math.PI;
}

export function geocentricToGeodetic(latDegrees: number): number {
  if (latDegrees === 90 || latDegrees === -90 || isNaN(latDegrees) || latDegrees == null) return latDegrees;
  const latRadians = (latDegrees * Math.PI) / 180;
  const geodeticLatRad = Math.atan(Math.tan(latRadians) / 0.99330562000984);
  return (geodeticLatRad * 180) / Math.PI;
}
